import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import { useState } from "react";

const AMOUNTS = [100, 250, 500, 1000, 2500];

// illustrative structure — labelled "illustrative" on-screen
function computeRow(amount) {
  return [
    {
      brand: "Instantxfer CashApp",
      fee: +(amount * 0.009).toFixed(2),
      spread: 0,
      arrive: "~ 2 min",
      you: true,
      note: "mid-market",
    },
    { brand: "Legacy bank wire", fee: 25, spread: +(amount * 0.032).toFixed(2), arrive: "3–5 days", note: "SWIFT" },
    { brand: "Big remittance app A", fee: +(amount * 0.019).toFixed(2), spread: +(amount * 0.021).toFixed(2), arrive: "1–2 hrs", note: "Mid + margin" },
    { brand: "Card processor", fee: +(amount * 0.029).toFixed(2), spread: +(amount * 0.025).toFixed(2), arrive: "10–30 min", note: "3.9% blended" },
  ];
}

export default function Comparison() {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(500);
  const rows = computeRow(amount);
  const best = rows[0];
  const worst = rows[1];
  const save = (worst.fee + worst.spread - best.fee - best.spread).toFixed(2);

  return (
    <section className="py-24 md:py-32 bg-canvas" data-testid="comparison">
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end mb-10">
          <div className="md:col-span-8">
            <div className="label-eyebrow">{t("home.comparison.eyebrow")}</div>
            <h2 className="mt-4 font-display font-medium text-forest text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest">
              {t("home.comparison.title")} <span className="italic font-serif">{t("home.comparison.italic")}</span>
            </h2>
          </div>
          <div className="md:col-span-4 text-ink/70">
            Move the slider — see the true cost on any transfer, then see what Instantxfer CashApp would keep in
            your recipient&apos;s pocket instead.
          </div>
        </div>

        <div className="card-flat p-6 md:p-10">
          {/* Slider */}
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div>
              <div className="label-eyebrow">Send amount</div>
              <div className="mt-2 font-display text-4xl md:text-5xl text-forest tracking-tightest">
                ${amount.toLocaleString()}
                <span className="text-ink/40 text-lg ml-2">USD</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {AMOUNTS.map((a) => (
                <button
                  key={a}
                  data-testid={`amount-${a}`}
                  onClick={() => setAmount(a)}
                  className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                    amount === a
                      ? "bg-forest text-canvas border-forest"
                      : "bg-canvas text-ink/70 border-forest/15 hover:bg-forest/5"
                  }`}
                >
                  ${a.toLocaleString()}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 hairline" />

          {/* Rows */}
          <div className="mt-6 grid grid-cols-1 gap-3">
            {rows.map((r, i) => {
              const total = r.fee + r.spread;
              return (
                <motion.div
                  key={r.brand}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className={`grid grid-cols-12 items-center gap-4 rounded-2xl px-4 md:px-6 py-4 border ${
                    r.you
                      ? "bg-forest text-canvas border-forest"
                      : "bg-white border-forest/10 text-ink"
                  }`}
                  data-testid={`compare-row-${i}`}
                >
                  <div className="col-span-12 md:col-span-4 flex items-center gap-3">
                    {r.you ? (
                      <Check className="w-4 h-4 text-lime" />
                    ) : (
                      <X className="w-4 h-4 text-ink/40" />
                    )}
                    <div>
                      <div className={`font-display text-lg ${r.you ? "text-canvas" : "text-forest"}`}>
                        {r.brand}
                      </div>
                      <div className={`text-[11px] tracking-[0.2em] uppercase ${r.you ? "text-lime" : "text-ink/50"}`}>
                        {r.note}
                      </div>
                    </div>
                  </div>
                  <Cell label="Fee" value={`$${r.fee.toFixed(2)}`} you={r.you} />
                  <Cell label="FX spread" value={r.spread ? `$${r.spread.toFixed(2)}` : "$0"} you={r.you} />
                  <Cell label="Arrives" value={r.arrive} you={r.you} />
                  <Cell
                    label="Total cost"
                    value={`$${total.toFixed(2)}`}
                    you={r.you}
                    bold
                  />
                </motion.div>
              );
            })}
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
            <div className="text-xs text-ink/50">
              Illustrative rates. Real quotes shown before you tap send.
            </div>
            <div className="rounded-full bg-lime text-ink px-5 py-2.5 text-sm font-semibold" data-testid="savings-pill">
              You&apos;d keep ${save} more on this send
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Cell({ label, value, you, bold }) {
  return (
    <div className="col-span-6 md:col-span-2">
      <div className={`text-[10px] tracking-[0.22em] uppercase ${you ? "text-canvas/50" : "text-ink/40"}`}>
        {label}
      </div>
      <div className={`mt-1 font-display ${bold ? "text-xl" : "text-base"} ${you ? "text-canvas" : "text-ink"}`}>
        {value}
      </div>
    </div>
  );
}
